/**
 * Dieren en voeding — het rekenblok met "Zet in antwoord".
 *
 * Er wordt hier **niets** gerekend. De knop Bereken stuurt dier,
 * gewicht, activiteit en product naar de backend, en die geeft het
 * antwoord van de FEDIAF-laag terug. Dat is met opzet: één rekenlaag
 * voor chat, mail en dit scherm, en geen tweede waarheid in
 * JavaScript.
 *
 * "Zet in antwoord" schuift de uitkomst naar de reactietegel van
 * bouwer 2 via `antwoordBrug`. Die tegel bestond nog niet toen dit
 * gebouwd werd; het contract staat in `VERZOEK.md`. Zolang de tegel er
 * niet is, toont dit blok zelf wát er klaarstaat — zichtbaar, niet
 * stil.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { vertaalApiFout } from "@/lib/api";
import {
  werkplekKlantenApi,
  type WerkplekDier,
  type WerkplekVoedingUitkomst,
} from "@/lib/werkplek-api";

import { huidigVoorstel, zetInAntwoord } from "./antwoordBrug";
import { datumKort, leeftijdTekst, onbekendTekst } from "./opmaak";

/**
 * Producten om uit te kiezen. De productenlijst zelf hangt aan een
 * ander scherm; zolang die er niet is krijgt dit blok hem als prop,
 * en zonder producten staat de knop uit mét de reden.
 */
export interface VoedingProduct {
  id: string;
  naam: string;
  herkomst?: string | null;
  rekenbaar?: boolean;
}

const ACTIVITEITEN: { waarde: string; label: string }[] = [
  { waarde: "", label: "Uit het profiel" },
  { waarde: "laag", label: "Rustig, senior" },
  { waarde: "normaal", label: "Normaal" },
  { waarde: "hoog", label: "Actief" },
];

export function VoedingsBlok({
  klantId,
  dieren,
  producten: meegegeven,
}: {
  klantId: string;
  dieren: WerkplekDier[];
  producten?: VoedingProduct[];
}) {
  const levend = dieren;
  const [dierId, setDierId] = useState(levend[0]?.id ?? "");
  // Sessie 46 (B9): de productenlijst komt nu zelf mee, eerder gekocht
  // voorop. Tot 23-09 kreeg dit blok nooit producten en stond Bereken
  // altijd uit.
  const lijst = useQuery({
    queryKey: ["werkplek", "voeding-producten", klantId],
    queryFn: () => werkplekKlantenApi.voedingProducten(klantId),
    enabled: meegegeven === undefined,
  });
  const producten: VoedingProduct[] = meegegeven ?? lijst.data ?? [];
  // Een dier dat net is toegevoegd of weggehaald: kies er een dat bestaat.
  useEffect(() => {
    if (!levend.some((d) => d.id === dierId)) setDierId(levend[0]?.id ?? "");
  }, [levend, dierId]);
  // Bewust leeg beginnen, niet stil het eerste product kiezen: dan
  // zegt het menu "Kies een product" terwijl er al een gekozen is, en
  // rekent de knop met iets wat niemand heeft aangewezen.
  const [productId, setProductId] = useState("");
  // Sessie 47 (6b): eerder gekocht vooraan (zo komt de lijst al binnen),
  // daarna zoeken in de rest.
  const [zoek, setZoek] = useState("");
  const [volwassen, setVolwassen] = useState("");
  const [gewicht, setGewicht] = useState("");
  const [activiteit, setActiviteit] = useState("");
  const [uitkomst, setUitkomst] = useState<WerkplekVoedingUitkomst | null>(
    null,
  );
  const [gezet, setGezet] = useState<string | null>(null);

  const dier = levend.find((d) => d.id === dierId) ?? null;
  const isPup =
    dier !== null &&
    dier.diersoort === "hond" &&
    dier.leeftijd_maanden !== null &&
    dier.leeftijd_maanden < 12;
  const naald = zoek.trim().toLowerCase();
  const getoond = naald
    ? producten.filter(
        (p) => p.id === productId || p.naam.toLowerCase().includes(naald),
      )
    : producten;

  const bereken = useMutation({
    mutationFn: () =>
      werkplekKlantenApi.berekenVoeding(klantId, {
        huisdier_id: dierId,
        product_item_id: productId,
        gewicht_kg: gewicht.trim() === "" ? null : Number(gewicht.replace(",", ".")),
        activiteitsniveau: activiteit === "" ? null : activiteit,
        verwacht_volwassen_gewicht_kg:
          volwassen.trim() === "" ? null : Number(volwassen.replace(",", ".")),
      }),
    onSuccess: (data) => {
      setUitkomst(data);
      setGezet(null);
    },
  });

  if (levend.length === 0) {
    return (
      <p className="wp-stil">
        Voeg eerst een dier toe; dan kan er gerekend worden.
      </p>
    );
  }

  // Uitgeschakeld = uitleggen waarom. Nooit een knop die stil niets doet.
  const redenUit =
    productId === "" && lijst.isLoading
      ? "Producten worden geladen."
      : productId === "" && producten.length === 0
        ? "Geen producten gevonden om mee te rekenen."
        : productId === ""
      ? "Kies eerst een product. Zonder product is er geen energiewaarde om mee te rekenen."
      : dierId === ""
        ? "Kies eerst een dier."
        : null;

  return (
    <>
      <div className="wp-calc">
        <div>
          <label htmlFor="wp-voeding-dier">Dier</label>
          <select
            id="wp-voeding-dier"
            value={dierId}
            onChange={(e) => {
              setDierId(e.target.value);
              setUitkomst(null);
              setGezet(null);
            }}
          >
            {levend.map((d) => (
              <option key={d.id} value={d.id}>
                {d.naam}
                {d.ras ? ` · ${d.ras}` : ""} · {leeftijdTekst(d.leeftijd_maanden)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="wp-voeding-gewicht">Gewicht (kg)</label>
          <input
            id="wp-voeding-gewicht"
            inputMode="decimal"
            value={gewicht}
            placeholder={
              dier?.gewicht_kg != null ? String(dier.gewicht_kg) : "onbekend"
            }
            onChange={(e) => setGewicht(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="wp-voeding-activiteit">Activiteit</label>
          <select
            id="wp-voeding-activiteit"
            value={activiteit}
            onChange={(e) => setActiviteit(e.target.value)}
          >
            {ACTIVITEITEN.map((a) => (
              <option key={a.waarde} value={a.waarde}>
                {a.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="wp-voeding-product">Product</label>
          <select
            id="wp-voeding-product"
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              setUitkomst(null);
              setGezet(null);
            }}
          >
            <option value="">
              {naald && getoond.length === 0 ? "Geen product gevonden" : "Kies een product"}
            </option>
            {getoond.map((p) => (
              <option key={p.id} value={p.id} disabled={p.rekenbaar === false}>
                {p.naam}
                {p.herkomst ? ` · ${p.herkomst}` : ""}
                {p.rekenbaar === false ? " · niet door te rekenen" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="wp-calc">
        <div className="wp-calc-breed">
          <label htmlFor="wp-voeding-zoek">
            Zoek een product (eerder gekocht staat vooraan)
          </label>
          <input
            id="wp-voeding-zoek"
            type="search"
            value={zoek}
            placeholder="merk of productnaam"
            onChange={(e) => setZoek(e.target.value)}
          />
        </div>
        {isPup ? (
          <div className="wp-calc-breed">
            <label htmlFor="wp-voeding-volwassen">
              Verwacht volwassen gewicht (kg), nodig voor een pup
            </label>
            <input
              id="wp-voeding-volwassen"
              inputMode="decimal"
              value={volwassen}
              placeholder={
                dier?.verwacht_volwassen_gewicht_kg != null
                  ? String(dier.verwacht_volwassen_gewicht_kg)
                  : "bijv. 40"
              }
              onChange={(e) => setVolwassen(e.target.value)}
            />
          </div>
        ) : null}
      </div>

      {dier && dier.onbekend.length > 0 ? (
        <p className="wp-stil">{onbekendTekst(dier.onbekend)}</p>
      ) : null}

      <div className="wp-knoprij">
        <button
          type="button"
          className="wp-knop wp-knop-primair"
          disabled={redenUit !== null || bereken.isPending}
          onClick={() => bereken.mutate()}
        >
          {bereken.isPending ? "Bezig met rekenen" : "Bereken"}
        </button>
        {redenUit ? <span className="wp-stil">{redenUit}</span> : null}
      </div>

      {bereken.isError ? (
        <p className="wp-stil" role="alert">
          {vertaalApiFout(bereken.error, "De berekening lukte niet.")}
        </p>
      ) : null}

      {uitkomst ? (
        <Uitkomst
          klantId={klantId}
          uitkomst={uitkomst}
          gezet={gezet}
          onZet={(tekst) => {
            zetInAntwoord({
              bron: "voedingsblok",
              klantId,
              tekst,
              moment: new Date().toISOString(),
            });
            setGezet(huidigVoorstel()?.tekst ?? tekst);
          }}
        />
      ) : null}
    </>
  );
}

function Uitkomst({
  uitkomst,
  gezet,
  onZet,
}: {
  klantId: string;
  uitkomst: WerkplekVoedingUitkomst;
  gezet: string | null;
  onZet: (tekst: string) => void;
}) {
  // Geen uitkomst? Dan geen verzonnen getal, maar de reden.
  if (uitkomst.gram_per_dag === null) {
    return (
      <p className="wp-stil" role="status">
        {uitkomst.reden ?? "Deze combinatie kan niet doorgerekend worden."}
      </p>
    );
  }

  return (
    <>
      <div className="wp-calc-uit">
        <div>
          <b>{uitkomst.gram_per_dag} g</b>
          <span>per dag</span>
        </div>
        <div>
          <b>{uitkomst.dagen_per_zak ?? "onbekend"}</b>
          <span>
            {uitkomst.dagen_per_zak ? "dagen gaat een zak mee" : "zakinhoud onbekend"}
          </span>
        </div>
        <div>
          <b>{uitkomst.volgende_zak_op ? datumKort(uitkomst.volgende_zak_op) : "onbekend"}</b>
          <span>volgende zak nodig</span>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button
            type="button"
            className="wp-knop"
            disabled={!uitkomst.antwoordtekst}
            onClick={() => onZet(uitkomst.antwoordtekst as string)}
          >
            Zet in antwoord
          </button>
        </div>
      </div>

      {uitkomst.afvallen ? (
        <p className="wp-stil" data-testid="voeding-afvallen">
          {`Startpunt om af te vallen, op het streefgewicht van ${String(uitkomst.streefgewicht_kg ?? "").replace(".", ",")} kg.`}
          {uitkomst.eiwit_code === "eiwit_te_laag"
            ? " Te weinig eiwit bij deze hoeveelheid: een afvalvoer of eiwitrijker voer past beter."
            : uitkomst.eiwit_code === "eiwit_onbekend"
              ? " Eiwitgehalte van dit voer onbekend."
              : ""}
        </p>
      ) : null}

      {uitkomst.aannames.length > 0 ? (
        <ul className="wp-stil" style={{ paddingLeft: 18, margin: "4px 0" }}>
          {uitkomst.aannames.map((aanname) => (
            <li key={aanname}>{aanname}</li>
          ))}
        </ul>
      ) : null}

      {gezet ? (
        <p className="wp-stil" role="status">
          Staat klaar voor de reactietegel: &ldquo;{gezet}&rdquo;
        </p>
      ) : null}
    </>
  );
}
